import React, { useRef, useMemo, useEffect, useCallback, useState, } from 'react';
import { useOnOutsideClick } from '@wix/da-hooks/pkg/useOnOutsideClick';
import { DropdownStateContext, } from './DropdownStateContext';
import { useDropdownState } from '../hooks/useDropdownState';
import { keyEventToHotkey, DropdownHotkey } from '../hotkeys';
// This component manages your dropdown state (open/close) and
// attaches some event handlers. Use if you need to build something custom
var DropdownStateManager = function (_a) {
    var children = _a.children, onOpen = _a.onOpen, onClose = _a.onClose, onToggle = _a.onToggle, disabled = _a.disabled;
    // state purely used to trigger effect re-run when toggle node changes
    var _b = useState(null), toggleNode = _b[0], setToggleNode = _b[1];
    var toggleRef = useRef(null);
    var menuRef = useRef(null);
    var _c = useState(null), firstKeyInteraction = _c[0], setFirstKeyInteraction = _c[1];
    var _d = useDropdownState(menuRef, toggleRef, {
        disabled: disabled,
        onOpen: onOpen,
        onClose: onClose,
        onToggle: onToggle,
    }), isOpen = _d.isOpen, openDropdown = _d.openDropdown, closeDropdown = _d.closeDropdown, toggleDropdown = _d.toggleDropdown;
    useOnOutsideClick([toggleRef, menuRef], closeDropdown);
    var handleCloseDropdown = useCallback(function () {
        closeDropdown();
        setFirstKeyInteraction(null);
    }, [closeDropdown]);
    var handleKeyDown = useCallback(function (e) {
        if ((!toggleRef.current ||
            !toggleRef.current.contains(document.activeElement)) &&
            (!menuRef.current || !menuRef.current.contains(document.activeElement))) {
            return;
        }
        var activeHotkey = keyEventToHotkey(e);
        switch (activeHotkey) {
            // Space and enter are handled by the default onClick handler, no need
            // to manually trigger the dropdown here.
            case DropdownHotkey.SPACE:
            case DropdownHotkey.ENTER:
                // This `isOpen` branch allows us to close the dropdown
                // via the keyboard even if it was opened with the mouse
                if (isOpen) {
                    handleCloseDropdown();
                    e.preventDefault();
                }
                else {
                    setFirstKeyInteraction(activeHotkey);
                    openDropdown();
                    e.preventDefault();
                }
                break;
            case DropdownHotkey.ARROW_UP:
            case DropdownHotkey.ARROW_DOWN:
                if (!isOpen) {
                    setFirstKeyInteraction(activeHotkey);
                    openDropdown();
                    e.preventDefault();
                }
                else if (isOpen && !firstKeyInteraction) {
                    setFirstKeyInteraction(activeHotkey);
                    e.preventDefault();
                }
                break;
            case DropdownHotkey.HOME:
            case DropdownHotkey.END:
                // HOME and END can't open the dropdown, but we need to
                // support jumping to the first and last item if the dropdown has
                // been opened with the mouse.
                if (isOpen) {
                    setFirstKeyInteraction(activeHotkey);
                    e.preventDefault();
                }
                break;
            // A11Y TODO: Figure out how to allow tabs for StandardMenus inside of dialogs
            case DropdownHotkey.ESCAPE:
            case DropdownHotkey.TAB:
                handleCloseDropdown();
                break;
            default:
        }
    }, [handleCloseDropdown, isOpen, openDropdown, firstKeyInteraction]);
    var context = useMemo(function () { return ({
        isOpen: isOpen,
        openDropdown: openDropdown,
        closeDropdown: handleCloseDropdown,
        toggleDropdown: toggleDropdown,
        setToggleRef: function (node) {
            toggleRef.current = node;
            setToggleNode(node);
        },
        setMenuRef: function (node) { return (menuRef.current = node); },
        firstKeyInteraction: firstKeyInteraction,
    }); }, [
        isOpen,
        openDropdown,
        handleCloseDropdown,
        toggleDropdown,
        firstKeyInteraction,
    ]);
    // attach toggle events
    useEffect(function () {
        var toggle = toggleRef.current;
        if (!toggle || disabled) {
            return;
        }
        toggle.addEventListener('click', toggleDropdown);
        toggle.addEventListener('keydown', handleKeyDown);
        return function () {
            toggle.removeEventListener('click', toggleDropdown);
            toggle.removeEventListener('keydown', handleKeyDown);
        };
    }, [toggleDropdown, disabled, handleKeyDown, toggleNode]);
    // The menu ref element is what gets focused when the dropdown is opened via mouse. In that case, we have to listen for the
    // keydown event there and call the `handleKeyDown` callback accordingly. Focus jumps to the menu after `firstKeyInteraction` gets set,
    // so we remove the listener to prevent duplicate calls to `handleKeyDown`
    useEffect(function () {
        var menu = menuRef.current;
        if (!menu) {
            return;
        }
        if (isOpen && !firstKeyInteraction) {
            menu.addEventListener('keydown', handleKeyDown);
            return function () {
                menu.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [handleKeyDown, isOpen, firstKeyInteraction]);
    return (React.createElement(DropdownStateContext.Provider, { value: context }, children));
};
DropdownStateManager.displayName = 'DropdownStateManager';
export default DropdownStateManager;
//# sourceMappingURL=DropdownStateManager.js.map